import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Thermometer, Droplets, CheckCircle, AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient } from '@/services/client';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ResponsiveContainer, CartesianGrid, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';

interface EnvironmentMonitoringProps {
  batchId: string | null;
  batchDay: number | null;
}

const STAGE_TARGETS = [
  { label: 'Brooding (D1-7)', min: 33, max: 35, dayStart: 1, dayEnd: 7 },
  { label: 'Week 2 (D8-14)', min: 30, max: 32, dayStart: 8, dayEnd: 14 },
  { label: 'Week 3 (D15-21)', min: 27, max: 29, dayStart: 15, dayEnd: 21 },
  { label: 'Grower (D22-28)', min: 24, max: 26, dayStart: 22, dayEnd: 28 },
  { label: 'Finisher (D29+)', min: 20, max: 24, dayStart: 29, dayEnd: 999 },
];

const getTargetForDay = (day: number) =>
  STAGE_TARGETS.find((s) => day >= s.dayStart && day <= s.dayEnd) ?? STAGE_TARGETS[4];

const getTempColor = (temp: number | null, target: { min: number; max: number }) => {
  if (temp == null) return 'text-muted-foreground';
  if (temp >= target.min && temp <= target.max) return 'text-success';
  if (temp >= target.min - 2 && temp <= target.max + 2) return 'text-yellow-500';
  if (temp >= target.min - 4 && temp <= target.max + 4) return 'text-orange-500';
  return 'text-destructive';
};

const getGaugeStroke = (temp: number | null, target: { min: number; max: number }) => {
  if (temp == null) return 'hsl(var(--muted-foreground))';
  if (temp >= target.min && temp <= target.max) return 'hsl(var(--success))';
  if (temp >= target.min - 2 && temp <= target.max + 2) return 'hsl(210, 60%, 55%)';
  if (temp >= target.min - 4 && temp <= target.max + 4) return 'hsl(30, 80%, 55%)';
  return 'hsl(var(--destructive))';
};

export const EnvironmentMonitoring: React.FC<EnvironmentMonitoringProps> = ({ batchId, batchDay }) => {
  const { data: dailyRecords, isLoading } = useQuery({
    queryKey: ['daily-records-env', batchId],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data } = await client
        .from('daily_records')
        .select('record_date, temperature_min_c, temperature_max_c, humidity_pct, mortality_count, feed_consumed_kg, water_consumed_liters, weight_sample_avg_g, batch_day')
        .eq('batch_id', batchId!)
        .order('record_date', { ascending: false })
        .limit(14);
      return data ?? [];
    },
    enabled: !!batchId,
    staleTime: 1000 * 60 * 5,
  });

  const currentTarget = getTargetForDay(batchDay ?? 1);
  const latest = dailyRecords?.[0];
  const currentTemp = latest?.temperature_max_c ?? latest?.temperature_min_c ?? null;
  const currentHumidity = latest?.humidity_pct ?? null;

  const chartData = useMemo(() => {
    if (!dailyRecords) return [];
    return [...dailyRecords].reverse().map((r) => ({
      date: r.record_date.slice(5), // MM-DD
      min: r.temperature_min_c,
      max: r.temperature_max_c,
    }));
  }, [dailyRecords]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <Skeleton className="h-40 w-40 mx-auto rounded-full" />
        </CardContent>
      </Card>
    );
  }

  // Gauge SVG
  const gaugeRadius = 60;
  const circumference = Math.PI * gaugeRadius; // half circle
  const tempMin = 10;
  const tempMax = 45;
  const clampedTemp = currentTemp != null ? Math.max(tempMin, Math.min(tempMax, currentTemp)) : tempMin;
  const fraction = (clampedTemp - tempMin) / (tempMax - tempMin);
  const strokeDash = fraction * circumference;

  return (
    <div className="space-y-4">
      {/* Temperature Gauge */}
      <Card>
        <CardContent className="py-6 flex flex-col items-center gap-4">
          <div className="relative">
            <svg width="160" height="90" viewBox="0 0 160 90">
              {/* Background arc */}
              <path
                d="M 10 80 A 60 60 0 0 1 150 80"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="12"
                strokeLinecap="round"
              />
              {/* Value arc */}
              <path
                d="M 10 80 A 60 60 0 0 1 150 80"
                fill="none"
                stroke={getGaugeStroke(currentTemp, currentTarget)}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${strokeDash} ${circumference}`}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
              <span className={cn('text-3xl font-bold', getTempColor(currentTemp, currentTarget))}>
                {currentTemp != null ? `${currentTemp}°C` : <span className="text-muted-foreground text-base font-normal">No sensor</span>}
              </span>
            </div>
          </div>

          {/* Humidity */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Droplets className="h-4 w-4" />
            <span className="text-sm">Humidity: {currentHumidity != null ? `${currentHumidity}%` : <span className="text-muted-foreground">No sensor</span>}</span>
          </div>
        </CardContent>
      </Card>

      {/* Target by stage */}
      <Card>
        <CardContent className="py-4 space-y-2">
          <h4 className="text-sm font-semibold text-foreground mb-2">Temperature Targets</h4>
          {STAGE_TARGETS.map((stage) => {
            const isCurrent = batchDay != null && batchDay >= stage.dayStart && batchDay <= stage.dayEnd;
            const inRange = currentTemp != null && currentTemp >= stage.min && currentTemp <= stage.max;
            return (
              <div
                key={stage.label}
                className={cn(
                  'flex items-center justify-between text-sm px-3 py-2 rounded-lg',
                  isCurrent ? 'bg-primary/10 border border-primary/30' : 'bg-muted/30'
                )}
              >
                <span className={cn(isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                  {stage.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-foreground font-medium">{stage.min}-{stage.max}°C</span>
                  {isCurrent && (
                    inRange
                      ? <CheckCircle className="h-4 w-4 text-success" />
                      : <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Temperature Trend Chart */}
      {chartData.length > 1 && (
        <Card>
          <CardContent className="py-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">14-Day Temperature Trend</h4>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip />
                <ReferenceLine y={currentTarget.min} stroke="hsl(var(--success))" strokeDasharray="5 5" label={{ value: `${currentTarget.min}°`, position: 'left', fontSize: 10 }} />
                <ReferenceLine y={currentTarget.max} stroke="hsl(var(--success))" strokeDasharray="5 5" label={{ value: `${currentTarget.max}°`, position: 'left', fontSize: 10 }} />
                <Line type="monotone" dataKey="min" stroke="hsl(210, 70%, 50%)" strokeWidth={2} dot={false} name="Morning" />
                <Line type="monotone" dataKey="max" stroke="hsl(25, 85%, 55%)" strokeWidth={2} dot={false} name="Evening" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Comparison: Your House vs Recommended */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-8">
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm text-muted-foreground">Your House</span>
              <Thermometer className={cn('h-12 w-12', getTempColor(currentTemp, currentTarget))} />
              <span className={cn('text-2xl font-bold', getTempColor(currentTemp, currentTarget))}>
                {currentTemp != null ? `${currentTemp}°C` : <span className="text-muted-foreground text-base font-normal">No sensor</span>}
              </span>
            </div>

            {currentTemp != null && (
              <div className="text-4xl text-muted-foreground">
                {currentTemp < currentTarget.min ? '<' : currentTemp > currentTarget.max ? '>' : '='}
              </div>
            )}

            <div className="flex flex-col items-center gap-2">
              <span className="text-sm text-muted-foreground">Recommended</span>
              <Thermometer className="h-12 w-12 text-success" />
              <span className="text-2xl font-bold text-success">
                {currentTarget.min}-{currentTarget.max}°C
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
